import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { getToken } from "../lib/api";
import AuthImage from "./AuthImage";
import { Upload, Download, RefreshCw, Loader2, Plus, X, Camera } from "lucide-react";
import { toast } from "sonner";

/**
 * SessionPhotosManager — Iter AB
 *
 * Renders the full session photo grid: 4 cardinal views × 2 phases (baseline +
 * post-treatment) + an optional movement-photo list per phase.
 *
 * Backwards compat: when a session has only the legacy `front_image / side_image /
 * back_image` fields (no `photos` object), we project them into `baseline.front /
 * left_side / back` for display, while uploads go into the new structured field.
 *
 * Persistence: every upload posts to `/api/photos/upload`, then PATCHes the
 * session with the full `photos` object so partial saves are atomic.
 */

const CARDINAL = [
  { key: "front",      label: "Front" },
  { key: "back",       label: "Back" },
  { key: "left_side",  label: "Left side" },
  { key: "right_side", label: "Right side" },
];

const PHASE_DEFS = [
  { key: "baseline", label: "Baseline", subtitle: "Pre-treatment" },
  { key: "post",     label: "Post-treatment", subtitle: "After hands-on" },
];

function legacyToPhotos(session) {
  // Build a photos object that merges any new `photos` field with legacy
  // *_image fields so existing sessions render without migration.
  const p = session?.photos || {};
  return {
    baseline_front: p.baseline_front || session?.front_image || null,
    baseline_back:  p.baseline_back  || session?.back_image  || null,
    // Legacy single side_image is the client's RIGHT side (guided order Front→Right→Back→Left).
    baseline_right_side: p.baseline_right_side || session?.side_image || null,
    baseline_left_side:  p.baseline_left_side  || null,
    baseline_movement: p.baseline_movement || [],
    post_front: p.post_front || null,
    post_back: p.post_back || null,
    post_left_side: p.post_left_side || null,
    post_right_side: p.post_right_side || null,
    post_movement: p.post_movement || [],
  };
}

export default function SessionPhotosManager({ session, onSessionUpdate, phases }) {
  const [photos, setPhotos] = useState(() => legacyToPhotos(session));
  const [movementPresets, setMovementPresets] = useState([]);
  const activePhases = phases ? PHASE_DEFS.filter((p) => phases.includes(p.key)) : PHASE_DEFS;

  // Keep local photos state in sync when the parent refreshes the session
  useEffect(() => {
    setPhotos(legacyToPhotos(session));
  }, [session]);

  // Pull movement-photo presets once
  useEffect(() => {
    api.get("/rom/presets")
      .then((r) => setMovementPresets(r.data.movement_presets || []))
      .catch(() => {});
  }, []);

  const persist = async (next) => {
    setPhotos(next);
    try {
      const r = await api.patch(`/sessions/${session.id}`, { photos: next });
      if (onSessionUpdate) onSessionUpdate(r.data);
    } catch (e) {
      toast.error("Could not save photo change.");
    }
  };

  const setCardinal = (phase, view, path) => {
    persist({ ...photos, [`${phase}_${view}`]: path });
  };

  const addMovement = (phase, movement) => {
    const key = `${phase}_movement`;
    persist({ ...photos, [key]: [...(photos[key] || []), movement] });
  };

  const removeMovement = (phase, id) => {
    const key = `${phase}_movement`;
    persist({ ...photos, [key]: (photos[key] || []).filter((m) => m.id !== id) });
  };

  const updateMovement = (phase, id, patch) => {
    const key = `${phase}_movement`;
    persist({
      ...photos,
      [key]: (photos[key] || []).map((m) => (m.id === id ? { ...m, ...patch } : m)),
    });
  };

  return (
    <div className="space-y-6" data-testid="session-photos-manager">
      {activePhases.map((phase) => (
        <PhaseBlock
          key={phase.key}
          phase={phase}
          photos={photos}
          movementPresets={movementPresets}
          setCardinal={setCardinal}
          addMovement={addMovement}
          removeMovement={removeMovement}
          updateMovement={updateMovement}
          clientName={session?.client_name}
          sessionDate={session?.created_at}
        />
      ))}
    </div>
  );
}

function PhaseBlock({ phase, photos, movementPresets, setCardinal, addMovement, removeMovement, updateMovement, clientName, sessionDate }) {
  const movements = photos[`${phase.key}_movement`] || [];
  const hasAny =
    !!photos[`${phase.key}_front`] ||
    !!photos[`${phase.key}_back`] ||
    !!photos[`${phase.key}_left_side`] ||
    !!photos[`${phase.key}_right_side`] ||
    movements.length > 0;

  return (
    <section
      className="rounded-3xl bg-white border border-paper-200 p-4 sm:p-6"
      data-testid={`photos-phase-${phase.key}`}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="label-overline text-sage-600">{phase.label}</div>
          <h3 className="font-heading text-xl font-medium text-ink-900">{phase.subtitle} photos</h3>
        </div>
        {!hasAny && (
          <span className="text-[10px] uppercase tracking-wider bg-paper-100 text-ink-500 px-2 py-1 rounded-full">No photos yet</span>
        )}
      </div>

      {/* Cardinal views */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {CARDINAL.map((view) => (
          <PhotoSlot
            key={view.key}
            phase={phase.key}
            view={view.key}
            label={view.label}
            path={photos[`${phase.key}_${view.key}`]}
            onUploaded={(path) => setCardinal(phase.key, view.key, path)}
            onCleared={() => setCardinal(phase.key, view.key, null)}
            clientName={clientName}
            sessionDate={sessionDate}
          />
        ))}
      </div>

      {/* Movement photos */}
      <MovementBlock
        phase={phase.key}
        movements={movements}
        presets={movementPresets}
        addMovement={(m) => addMovement(phase.key, m)}
        removeMovement={(id) => removeMovement(phase.key, id)}
        updateMovement={(id, patch) => updateMovement(phase.key, id, patch)}
      />
    </section>
  );
}

function PhotoSlot({ phase, view, label, path, onUploaded, onCleared, clientName, sessionDate }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const photoUrl = path && typeof path === "string" && path.includes("/")
    ? `${API}/photos/${path}?auth=${getToken()}`
    : null;

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image (JPG / PNG / WEBP)."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(up.data.path);
      toast.success(`${label} uploaded.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-paper-50 border border-paper-200 rounded-2xl p-3"
      data-testid={`photos-slot-${phase}-${view}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="label-overline">{label}</div>
        <div className="flex items-center gap-1">
          {photoUrl && (
            <>
              <a
                href={photoUrl}
                download={`${(clientName || "session").replace(/\s+/g, "_")}-${phase}-${view}-${(sessionDate || "").slice(0, 10)}.jpg`}
                target="_blank"
                rel="noopener noreferrer"
                title="Download"
                className="w-7 h-7 rounded-full hover:bg-white flex items-center justify-center text-ink-500 hover:text-ink-900"
                data-testid={`photos-download-${phase}-${view}`}
              >
                <Download className="w-3.5 h-3.5" />
              </a>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                title="Replace"
                className="w-7 h-7 rounded-full hover:bg-white flex items-center justify-center text-ink-500 hover:text-ink-900 disabled:opacity-50"
                data-testid={`photos-replace-${phase}-${view}`}
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </button>
              <button
                type="button"
                onClick={() => { if (window.confirm(`Remove ${label} photo?`)) onCleared(); }}
                title="Remove"
                className="w-7 h-7 rounded-full hover:bg-white flex items-center justify-center text-ink-500 hover:text-rose-700"
                data-testid={`photos-clear-${phase}-${view}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`photos-input-${phase}-${view}`}
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {path ? (
        <AuthImage path={path} alt={label} className="w-full h-28 sm:h-32 object-cover rounded-xl" />
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
          disabled={busy}
          data-testid={`photos-upload-${phase}-${view}`}
          className="w-full h-28 sm:h-32 rounded-xl bg-white border-2 border-dashed border-paper-300 hover:border-sage-500 hover:bg-sage-50/30 transition-colors flex flex-col items-center justify-center gap-1 text-ink-500 hover:text-ink-900 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-5 h-5" />}
          <span className="text-[11px] font-medium">{busy ? "Uploading…" : "Add photo"}</span>
        </button>
      )}
    </div>
  );
}

function MovementBlock({ phase, movements, presets, addMovement, removeMovement, updateMovement }) {
  const [showPresets, setShowPresets] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const usedLabels = useMemo(() => new Set(movements.map((m) => m.label)), [movements]);
  const availablePresets = presets.filter((p) => !usedLabels.has(p.label));

  const startUpload = (label) => {
    // Create a placeholder movement entry and immediately trigger the file picker.
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { toast.error("Max 10 MB."); return; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        addMovement({
          id: cryptoRandom(),
          label,
          path: up.data.path,
          phase,
          captured_at: new Date().toISOString(),
        });
      } catch (err) {
        toast.error("Upload failed.");
      }
    };
    input.click();
    setShowPresets(false);
    setCustomLabel("");
  };

  return (
    <div data-testid={`photos-movement-${phase}`}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div>
          <div className="label-overline">Movement photos (optional)</div>
          <p className="text-xs text-ink-500 mt-0.5">Capture ROM screens: cervical rotation, shoulder flexion, forward bend, squat, single-leg balance…</p>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowPresets((v) => !v)}
            data-testid={`photos-movement-add-${phase}`}
            className="min-h-[36px] px-3 rounded-full border border-paper-200 text-xs text-ink-700 hover:bg-paper-50 inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add movement photo
          </button>
          {showPresets && (
            <div
              className="absolute right-0 mt-1 w-[min(20rem,calc(100vw-2rem))] bg-white border border-paper-200 rounded-2xl shadow-lg z-10 p-1.5 max-h-80 overflow-y-auto"
              data-testid={`photos-movement-menu-${phase}`}
            >
              {availablePresets.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => startUpload(p.label)}
                  data-testid={`photos-movement-preset-${phase}-${p.id}`}
                  className="w-full text-left text-sm px-3 py-2 rounded-xl hover:bg-paper-50"
                >
                  {p.label}
                </button>
              ))}
              <div className="border-t border-paper-100 mt-1 pt-1">
                <div className="flex gap-1 px-1">
                  <input
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    placeholder="Custom label…"
                    className="flex-1 min-h-[36px] px-2 rounded-lg bg-paper-50 border border-paper-200 outline-none text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => customLabel.trim() && startUpload(customLabel.trim())}
                    className="min-h-[36px] px-3 rounded-lg bg-sage-500 hover:bg-sage-600 text-white text-xs font-medium"
                    data-testid={`photos-movement-custom-${phase}`}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {movements.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {movements.map((mv) => (
            <MovementCard
              key={mv.id}
              movement={mv}
              onRemove={() => removeMovement(mv.id)}
              onUpdate={(patch) => updateMovement(mv.id, patch)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MovementCard({ movement, onRemove, onUpdate }) {
  return (
    <div
      className="bg-paper-50 border border-paper-200 rounded-2xl p-3"
      data-testid={`photos-movement-card-${movement.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <input
          value={movement.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
          className="flex-1 min-w-0 text-xs font-medium bg-transparent border-0 outline-none px-0 text-ink-900"
          data-testid={`photos-movement-label-${movement.id}`}
        />
        <button
          type="button"
          onClick={onRemove}
          className="w-6 h-6 rounded-full hover:bg-rose-50 text-ink-500 hover:text-rose-700 flex items-center justify-center shrink-0"
          aria-label="Remove"
          data-testid={`photos-movement-remove-${movement.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <AuthImage path={movement.path} alt={movement.label} className="w-full h-28 object-cover rounded-xl" />
    </div>
  );
}

function cryptoRandom() {
  // Inexpensive 12-char id (no need for full uuid for movement entries the server doesn't index)
  return Array.from({ length: 12 }, () => Math.floor(Math.random() * 36).toString(36)).join("");
}
